import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BadgeAlert, Building2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';

function money(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function statusTone(status) {
  return String(status || '').toLowerCase() === 'active'
    ? 'bg-emerald-100 text-emerald-800'
    : 'bg-slate-100 text-slate-700';
}

export default function BudgetBranches() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    code: '',
    location: '',
    opening_balance: '',
    status: 'active',
    currency: user?.default_currency || 'UGX',
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/budget/branches');
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to load branches');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const branches = useMemo(() => Array.isArray(data?.branches) ? data.branches : [], [data?.branches]);
  const summary = data?.summary || {};
  const canManage = !!data?.can_manage;
  const canView = data?.can_view !== false;

  const resetForm = () => {
    setForm({
      name: '',
      code: '',
      location: '',
      opening_balance: '',
      status: 'active',
      currency: user?.default_currency || 'UGX',
    });
  };

  const openCreate = () => {
    setEditing(null);
    resetForm();
    setOpen(true);
  };

  const editBranch = (branch) => {
    setEditing(branch);
    setForm({
      name: branch.name || '',
      code: branch.code || '',
      location: branch.location || '',
      opening_balance: branch.opening_balance ?? '',
      status: branch.status || 'active',
      currency: branch.currency || user?.default_currency || 'UGX',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Branch name is required.');
    setSaving(true);
    try {
      const payload = {
        ...form,
        opening_balance: Number(form.opening_balance || 0),
      };
      if (editing?.id) {
        await api.put(`/budget/branches/${editing.id}`, payload);
        toast.success('Branch updated');
      } else {
        await api.post('/budget/branches', payload);
        toast.success('Branch created');
      }
      setOpen(false);
      setEditing(null);
      resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to save branch');
    } finally {
      setSaving(false);
    }
  };

  const disableBranch = async (branch) => {
    try {
      await api.post(`/budget/branches/${branch.id}/disable`);
      toast.success('Branch disabled');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to disable branch');
    }
  };

  const deleteBranch = async (branch) => {
    if (!window.confirm('Delete this branch? This is only allowed if no transactions exist.')) return;
    try {
      await api.delete(`/budget/branches/${branch.id}`);
      toast.success('Branch deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to delete branch');
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (data?.upgrade_required) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <BadgeAlert size={22} className="text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Branches</h1>
              <p className="text-sm text-slate-500">Upgrade your package to unlock Budget and Financial Intelligence.</p>
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
            {data?.message || 'This feature is available on premium packages only.'}
          </div>
          <div className="flex gap-2">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => navigate('/dashboard/package')}>
              View Packages
            </Button>
            <Button variant="outline" className="border-slate-200" onClick={load}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!canView) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center">
              <Building2 size={22} className="text-slate-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Branches</h1>
              <p className="text-sm text-slate-500">You do not have permission to view branch management.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Branches
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage branches for budgets, transactions, loans, and reporting.</p>
        </div>
        {canManage ? (
          <div className="flex gap-2">
            <Button variant="outline" className="border-slate-200" onClick={load}>
              <RefreshCw size={16} className="mr-1.5" /> Refresh
            </Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={openCreate}>
              <Plus size={16} className="mr-1.5" /> Add Branch
            </Button>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Total Branches</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{summary.total_branches || branches.length || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Active Branches</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{summary.active_branches || branches.filter((branch) => String(branch.status || '').toLowerCase() === 'active').length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Total Income</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(summary.total_income || 0, user?.default_currency || 'UGX')}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Total Expenses</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(summary.total_expenses || 0, user?.default_currency || 'UGX')}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Combined Balance</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(summary.combined_balance || 0, user?.default_currency || 'UGX')}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Branch List</CardTitle>
          <Badge variant="outline" className="border-slate-200 text-slate-600 bg-slate-50">{branches.length} item(s)</Badge>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-3 pr-4 font-medium">Branch Name</th>
                  <th className="py-3 pr-4 font-medium">Status</th>
                  <th className="py-3 pr-4 font-medium">Total Income</th>
                  <th className="py-3 pr-4 font-medium">Total Expense</th>
                  <th className="py-3 pr-4 font-medium">Current Balance</th>
                  <th className="py-3 pr-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {branches.map((branch) => (
                  <tr key={branch.id} className="border-b border-slate-50 last:border-0">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-slate-900">{branch.name}</div>
                      <div className="text-xs text-slate-500">{branch.location || branch.code || 'Main Branch'}</div>
                    </td>
                    <td className="py-3 pr-4">
                      <Badge className={statusTone(branch.status)}>{branch.status || 'active'}</Badge>
                    </td>
                    <td className="py-3 pr-4 text-slate-700">{money(branch.total_income || 0, branch.currency || user?.default_currency || 'UGX')}</td>
                    <td className="py-3 pr-4 text-slate-700">{money(branch.total_expense || 0, branch.currency || user?.default_currency || 'UGX')}</td>
                    <td className={`py-3 pr-4 font-semibold mono-nums ${(Number(branch.current_balance || 0) >= 0) ? 'text-emerald-700' : 'text-red-600'}`}>
                      {money(branch.current_balance || 0, branch.currency || user?.default_currency || 'UGX')}
                    </td>
                    <td className="py-3 pr-4">
                      {canManage ? (
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" className="border-slate-200" onClick={() => editBranch(branch)}>Edit</Button>
                          <Button size="sm" variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50" onClick={() => disableBranch(branch)} disabled={String(branch.status || '').toLowerCase() === 'disabled'}>
                            Disable
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteBranch(branch)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">View only</span>
                      )}
                    </td>
                  </tr>
                ))}
                {branches.length === 0 && (
                  <tr>
                    <td className="py-8 text-center text-slate-500" colSpan={6}>No branches yet. Main Branch will be created automatically.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Branch' : 'Add Branch'}</DialogTitle>
            <DialogDescription>Create and manage branch balances for budgets and transactions.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Branch Name</Label>
              <Input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="Example: Main Branch" />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Code (optional)</Label>
                <Input value={form.code} onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))} placeholder="BR-001" />
              </div>
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Location (optional)</Label>
              <Input value={form.location} onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))} placeholder="Branch location" />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Opening Balance</Label>
                <Input type="number" value={form.opening_balance} onChange={(e) => setForm((prev) => ({ ...prev, opening_balance: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Currency</Label>
                <Input value={form.currency} onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value.toUpperCase() }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Save Branch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
